import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ColumnConfig } from '../domain/column_config.model';
import * as XLSX from 'xlsx-js-style';

@Injectable({
  providedIn: 'root'
})
export class ExportExcelService {

  export(items: any[], columns: ColumnConfig[], filename: string = 'export', summary?: string): void {
    const exportColumns = columns.filter(c => c.type !== 'actions' && c.field !== 'itemId');

    // Headers
    const headers = exportColumns.map(c => c.header);

    // Data rows
    const rows = items.map(item =>
      exportColumns.map(col => this.resolveValue(item, col))
    );

    const ws: XLSX.WorkSheet = {};

    // Header row con estilo
    headers.forEach((h, i) => {
      const cell = XLSX.utils.encode_cell({ r: 0, c: i });
      ws[cell] = {
        v: h,
        t: 's',
        s: {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { fgColor: { rgb: '2563EB' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: {
            top: { style: 'thin', color: { rgb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
            left: { style: 'thin', color: { rgb: 'FFFFFF' } },
            right: { style: 'thin', color: { rgb: 'FFFFFF' } },
          }
        }
      };
    });

    // Data rows con estilo alternado
    rows.forEach((row, r) => {
      const isEven = r % 2 === 0;
      row.forEach((val, c) => {
        const cell = XLSX.utils.encode_cell({ r: r + 1, c });
        ws[cell] = {
          v: val.v,
          t: val.t,
          s: {
            fill: { fgColor: { rgb: isEven ? 'F1F5F9' : 'FFFFFF' } },
            alignment: { horizontal: val.t === 'n' ? 'right' : 'left', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: 'E2E8F0' } },
              bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
              left: { style: 'thin', color: { rgb: 'E2E8F0' } },
              right: { style: 'thin', color: { rgb: 'E2E8F0' } },
            }
          }
        };
      });
    });

    // Summary con estilo
    if (summary) {
      const summaryRow = rows.length + 2;
      const cell = XLSX.utils.encode_cell({ r: summaryRow, c: 0 });
      ws[cell] = {
        v: summary,
        t: 's',
        s: {
          font: { bold: true, italic: true, sz: 11 },
          fill: { fgColor: { rgb: 'EFF6FF' } },
          alignment: { horizontal: 'right' }
        }
      };

      // Merge summary across all columns
      if (!ws['!merges']) ws['!merges'] = [];
      ws['!merges'].push({
        s: { r: summaryRow, c: 0 },
        e: { r: summaryRow, c: headers.length - 1 }
      });
    }

    // Auto ancho de columnas basado en contenido real
    ws['!cols'] = exportColumns.map((col, i) => {
      const headerLen = col.header.length;
      const maxContentLen = rows.reduce((max, row) => {
        const val = String(row[i].v ?? '');
        return Math.max(max, val.length);
      }, 0);

      return {
        wch: Math.max(headerLen + 5, maxContentLen + 4)
      };
    });

    // Rango de la hoja
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rows.length + 3, c: headers.length - 1 }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, filename);
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  private resolveValue(item: any, col: ColumnConfig): { v: any, t: string } {
    const value = this.resolveField(item, col.field);

    switch (col.type) {
      case 'date':
        return {
          v: value ? new DatePipe('es-MX').transform(value, 'dd/MMM/yyyy h:mm a') ?? '' : '',
          t: 's'
        };
      case 'number':
        return {
          v: value != null ? Number(value) : 0,
          t: 'n'
        };
      default:
        return { v: value ?? '', t: 's' };
    }
  }

  private resolveField(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }
}
